// Peer benchmarking: the Desk Dashboard side of `wealth.portfolio.benchmarking.peer`
// (ADR-0027). It gives the performance figure of the tape, the two return
// columns of the client table, and the card of the clients against their ARC
// band.
import { lazy } from 'react';
import type { StatStripItem } from '@/components/ui/StatStrip';
import { defineWebFeature } from '@/features/kit';
import { performanceDetail } from '@/lib/desk-book-stats';
import { signTone, signedPct } from '@/lib/overview-figures';
import { benchmarkColumn, performanceColumn } from './arc-figures';

const ArcBandCard = lazy(() =>
    import('./benchmarking-peer.slots').then((m) => ({ default: m.ArcBandCard })),
);

export default defineWebFeature({
    id: 'wealth.portfolio.benchmarking.peer',
    slots: {
        'desk.dashboard.tape': [
            {
                key: 'book-return',
                order: 40,
                select: (_input, ctx): StatStripItem => ({
                    key: 'book-return',
                    label: 'Performance',
                    value: signedPct(ctx.stats.rangeReturnPct),
                    tone: signTone(ctx.stats.rangeReturnPct),
                    hint: `${performanceDetail({
                        rangeLabel: ctx.rangeLabel,
                        stats: ctx.stats,
                        benchmarkRange: ctx.dashboard?.benchmarkRange,
                        customPending: ctx.customPending,
                    })}. Opens Benchmarking.`,
                    to: '/desk/benchmarking',
                    testId: 'stat-book-return',
                }),
            },
        ],
        'desk.dashboard.cards': [
            {
                key: 'arc-band',
                order: 80,
                span: 12,
                rows: 'fit',
                title: 'Clients against their ARC band',
                summary: 'The return of each client in view against the ARC band of the client.',
                emptyBehaviour: 'keep',
                fitRows: (ctx) => ctx.clients.length,
                Component: ArcBandCard,
            },
        ],
        'desk.dashboard.clients.columns': [
            { key: 'performance', order: 10, column: (ctx) => performanceColumn(ctx.rangeLabel) },
            { key: 'benchmark', order: 20, column: (ctx) => benchmarkColumn(ctx.rangeLabel) },
        ],
    },
});
